// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {StatusSlate} from '@app/features/app/components/dialogs/shared/StatusSlate';
import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {BoostSubscriptionRow} from '@app/features/boost/components/BoostSubscriptionRow';
import {GuildBoostActionButton} from '@app/features/boost/components/GuildBoostActionButton';
import {GuildBoostBuyButton} from '@app/features/boost/components/GuildBoostBuyButton';
import {GuildBoostLevelTrack, GuildBoostStatusHero} from '@app/features/boost/components/GuildBoostLevelTrack';
import styles from '@app/features/boost/components/GuildBoostTab.module.css';
import {useGuildBoostAction} from '@app/features/boost/hooks/useGuildBoostAction';
import {useGuildBoostPurchase} from '@app/features/boost/hooks/useGuildBoostPurchase';
import BoostSubscription from '@app/features/boost/state/BoostSubscription';
import GuildBoosts from '@app/features/boost/state/GuildBoosts';
import {getGuildBoostLevelCards} from '@app/features/boost/utils/GuildBoostLevels';
import {
	LEVELS_DESCRIPTOR,
	LEVELS_EXPLANATION_DESCRIPTOR,
	NO_SLOTS_BUYABLE_DESCRIPTOR,
	SLOT_COOLDOWN_DESCRIPTOR,
} from '@app/features/boost/utils/GuildBoostMessages';
import {boostCooldownDays, removedSlotFreeAt} from '@app/features/boost/utils/GuildBoostPurchase';
import Guilds from '@app/features/guild/state/Guilds';
import {
	CANCEL_DESCRIPTOR,
	NEXT_DESCRIPTOR,
	SOMETHING_WENT_WRONG_DESCRIPTOR,
	TRY_AGAIN_DESCRIPTOR,
	TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR,
} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Spinner} from '@app/features/ui/components/Spinner';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {getFormattedDateTime, getFormattedShortDate} from '@app/features/user/utils/DateFormatting';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {WarningIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect} from 'react';

const PREVIOUS_DESCRIPTOR = msg({
	message: 'Previous',
	comment: 'Button that shows the previous page of a list.',
});
const REMOVE_BOOST_DESCRIPTOR = msg({
	message: 'Remove boost',
	comment: 'Button and confirmation title for taking your own boost off a community.',
});
const REMOVED_SLOT_WAITS_DESCRIPTOR = msg({
	message:
		"You can't use this boost slot on any community until {date}. A boost slot waits 7 days after it's put on a community.",
	comment:
		'Remove boost confirmation, when the freed slot is still in its 7-day wait. {date} is the local date and time the wait ends.',
});
const REMOVED_SLOT_FREE_DESCRIPTOR = msg({
	message: 'You can use the slot on another community right away.',
	comment: 'Remove boost confirmation, when the freed slot was put on the community more than 7 days ago.',
});
const GuildBoostTab: React.FC<{guildId: string}> = observer(({guildId}) => {
	const {i18n} = useLingui();
	const {acting, handleBoost, showError} = useGuildBoostAction();
	const purchase = useGuildBoostPurchase();
	useEffect(() => {
		void GuildBoosts.open(guildId);
		return GuildBoosts.close;
	}, [guildId]);
	// The account's boost subscription is managed here; it is read once per visit, where the shop runs.
	const shopRuns = RuntimeConfig.giftCodesEnabled;
	useEffect(() => {
		if (shopRuns) void BoostSubscription.load();
	}, [shopRuns]);
	const handleRemove = useCallback(
		(boostId: string) => {
			const freeAt = removedSlotFreeAt(GuildBoosts.slots, boostId, Date.now());
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={i18n._(REMOVE_BOOST_DESCRIPTOR)}
						description={
							<>
								<Trans>The level of this community can drop when your boost is removed.</Trans>{' '}
								{freeAt === null
									? null
									: freeAt > Date.now()
										? i18n._(REMOVED_SLOT_WAITS_DESCRIPTOR, {date: getFormattedDateTime(freeAt)})
										: i18n._(REMOVED_SLOT_FREE_DESCRIPTOR)}
							</>
						}
						primaryText={i18n._(REMOVE_BOOST_DESCRIPTOR)}
						primaryVariant="danger"
						secondaryText={i18n._(CANCEL_DESCRIPTOR)}
						onPrimary={async () => {
							try {
								await GuildBoosts.removeBoost(boostId);
							} catch {
								showError();
							}
						}}
						data-flx="boost.guild-boost-tab.handle-remove.confirm-modal"
					/>
				)),
			);
		},
		[i18n, showError],
	);
	const {status, ready, failed} = GuildBoosts;
	if (failed) {
		return (
			<StatusSlate
				Icon={WarningIcon}
				title={i18n._(SOMETHING_WENT_WRONG_DESCRIPTOR)}
				description={i18n._(TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR)}
				actions={[{text: i18n._(TRY_AGAIN_DESCRIPTOR), onClick: () => void GuildBoosts.open(guildId)}]}
				data-flx="boost.guild-boost-tab.status-slate"
			/>
		);
	}
	if (!ready || !status) {
		return (
			<div className={styles.spinnerContainer} data-flx="boost.guild-boost-tab.spinner-container">
				<Spinner data-flx="boost.guild-boost-tab.spinner" />
			</div>
		);
	}
	const ownBoostId = GuildBoosts.ownBoostIds[0];
	const cooldownDays = boostCooldownDays(GuildBoosts.slots, Date.now());
	const levels = getGuildBoostLevelCards(RuntimeConfig.limits, Guilds.getGuild(guildId)).filter(
		(level) => level.level > 0,
	);
	return (
		<div className={styles.container} data-flx="boost.guild-boost-tab.container">
			<GuildBoostStatusHero status={status} levels={levels}>
				{!GuildBoosts.hasFreeSlot && (
					<p className={styles.slotNote} data-flx="boost.guild-boost-tab.no-free-slot">
						{purchase.state === 'hidden' ? (
							<Trans>You have no free boost slots. Boost slots come with {PREMIUM_PRODUCT_NAME}.</Trans>
						) : (
							<span>{i18n._(NO_SLOTS_BUYABLE_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})}</span>
						)}
						{cooldownDays !== null && <span> {i18n._(SLOT_COOLDOWN_DESCRIPTOR, {count: cooldownDays})}</span>}
					</p>
				)}
				{ownBoostId && (
					<Button
						variant="secondary"
						small={true}
						disabled={acting}
						onClick={() => handleRemove(ownBoostId)}
						data-flx="boost.guild-boost-tab.button.remove"
					>
						{i18n._(REMOVE_BOOST_DESCRIPTOR)}
					</Button>
				)}
				<GuildBoostBuyButton />
				<GuildBoostActionButton acting={acting} onBoost={handleBoost} />
			</GuildBoostStatusHero>
			<BoostSubscriptionRow />
			<section className={styles.section} data-flx="boost.guild-boost-tab.perks-section">
				<div>
					<h2 className={styles.levelsHeading} data-flx="boost.guild-boost-tab.perks-heading">
						{i18n._(LEVELS_DESCRIPTOR)}
					</h2>
					<p className={styles.muted}>{i18n._(LEVELS_EXPLANATION_DESCRIPTOR)}</p>
				</div>
				<GuildBoostLevelTrack levels={levels} status={status} />
			</section>
			<section className={styles.section} data-flx="boost.guild-boost-tab.boosters-section">
				<h2 className={styles.levelsHeading} data-flx="boost.guild-boost-tab.boosters-heading">
					<Trans>Boosters</Trans>
				</h2>
				{GuildBoosts.page.length === 0 ? (
					<p className={styles.muted} data-flx="boost.guild-boost-tab.empty">
						<Trans>No boosts yet</Trans>
					</p>
				) : (
					<ul className={styles.boosters} data-flx="boost.guild-boost-tab.boosters">
						{GuildBoosts.page.map((boost) => (
							<li key={boost.id} className={styles.booster} data-flx="boost.guild-boost-tab.booster">
								<img
									src={AvatarUtils.getUserAvatarURL(boost.user, false)}
									alt=""
									className={styles.avatar}
									data-flx="boost.guild-boost-tab.avatar"
								/>
								<span className={styles.name} data-flx="boost.guild-boost-tab.name">
									{NicknameUtils.getDisplayName(boost.user)}
								</span>
								<span className={styles.muted} data-flx="boost.guild-boost-tab.boosted-at">
									{getFormattedShortDate(boost.created_at)}
								</span>
							</li>
						))}
					</ul>
				)}
				{(GuildBoosts.hasPrevious || GuildBoosts.hasMore) && (
					<div className={styles.actions} data-flx="boost.guild-boost-tab.pager">
						<Button
							variant="secondary"
							small={true}
							disabled={!GuildBoosts.hasPrevious || GuildBoosts.loading}
							onClick={GuildBoosts.previousPage}
							data-flx="boost.guild-boost-tab.button.previous-page"
						>
							{i18n._(PREVIOUS_DESCRIPTOR)}
						</Button>
						<Button
							variant="secondary"
							small={true}
							disabled={!GuildBoosts.hasMore || GuildBoosts.loading}
							onClick={GuildBoosts.nextPage}
							data-flx="boost.guild-boost-tab.button.next-page"
						>
							{i18n._(NEXT_DESCRIPTOR)}
						</Button>
					</div>
				)}
			</section>
		</div>
	);
});

export default GuildBoostTab;
